/**
 * Recommendation Types
 *
 * Types for the contextual recommendation engine (RECOMMEND pipeline stage).
 * Recommendations are generated after a note reaches COMPLETED status,
 * using RAG retrieval + LLM to suggest follow-up actions.
 *
 * The content field is polymorphic — its shape depends on the type:
 *   RELATED_NOTE:    { related_note_id, similarity_score, explanation }
 *   RESEARCH:        { suggestion, reasoning, keywords }
 *   ACTION_ITEM:     { action, priority, context }
 *   TOPIC_EXPANSION: { topic, questions, reasoning }
 */

/** Recommendation type categories — maps to backend RecommendationType enum */
export type RecommendationType =
  | "RELATED_NOTE"
  | "RESEARCH"
  | "ACTION_ITEM"
  | "TOPIC_EXPANSION";

/**
 * User rating for a recommendation.
 *   0 = Irrelevant (off-topic, not useful)
 *   1 = Poor (partially relevant, not actionable)
 *   2 = Useful (relevant and actionable)
 *   3 = Excellent (highly relevant, will follow up)
 *
 * Feeds into the eval flywheel: 3-rated recs are promoted to golden set,
 * patterns with avg < 1.0 are pruned.
 */
export type RecommendationRating = 0 | 1 | 2 | 3;

/** A single recommendation returned by the API */
export interface Recommendation {
  id: string;
  noteId: string;
  type: RecommendationType;
  /** Polymorphic payload — schema depends on type (see module docstring) */
  content: Record<string, unknown>;
  /** RAG-retrieved note IDs that informed this recommendation */
  sourceNoteIds: string[];
  /** LLM model that generated this (or "rag-only") */
  modelName: string;
  /** User's rating (null if unrated) */
  rating: RecommendationRating | null;
  ratedAt: string | null;
  createdAt: string;
}

/** API response for listing recommendations for a note */
export interface RecommendationListResponse {
  noteId: string;
  total: number;
  items: Recommendation[];
}

/** Request body for rating a recommendation */
export interface RateRecommendationRequest {
  rating: RecommendationRating;
}

/** Aggregated recommendation quality stats for the eval dashboard */
export interface RecommendationQualityStats {
  total: number;
  totalRated: number;
  avgRating: number;
  /** Rating distribution: { 0: count, 1: count, 2: count, 3: count } */
  distribution: Record<RecommendationRating, number>;
  /** Average rating per type: { RELATED_NOTE: 2.1, RESEARCH: 1.8, ... } */
  avgByType: Record<string, number>;
}

/** Labels for recommendation types (used in UI) */
export const REC_TYPE_LABELS: Record<RecommendationType, string> = {
  RELATED_NOTE: "Related Note",
  RESEARCH: "Research",
  ACTION_ITEM: "Action Item",
  TOPIC_EXPANSION: "Topic Expansion",
};

/** Labels for recommendation ratings (used in tooltips) */
export const RATING_LABELS: Record<RecommendationRating, string> = {
  0: "Irrelevant",
  1: "Poor",
  2: "Useful",
  3: "Excellent",
};
